import { ICategory } from '../../../interface/category';
import { IFilterOption } from '../../../interface/transaction';
import { ITransactionTypes } from '../../../interface/transactionTypes';

export const NO_CATEGORY_GROUP_ID = 'no-category';
const FALLBACK_LANGUAGE = 'en';
const MISSING_NAME = '-';

export interface ITransactionTypeGroup {
  id: string;
  name: string;
  transactionTypes: ITransactionTypes[];
}

const resolveCategoryName = (category: ICategory | undefined, appLanguage: string): string => {
  const translations = category?.translations ?? [];
  const translationIn = (language: string) =>
    translations.find((entry) => entry.language.startsWith(language))?.translation;
  return translationIn(appLanguage) ?? translationIn(FALLBACK_LANGUAGE) ?? MISSING_NAME;
};

const resolveCategoryId = (type: ITransactionTypes): string | undefined =>
  type.category?.id ?? type.categoryId ?? undefined;

export const groupTransactionTypesByCategory = (
  transactionTypes: ITransactionTypes[],
  appLanguage: string,
  noCategoryLabel: string,
): ITransactionTypeGroup[] => {
  if (transactionTypes.length === 0) {
    return [];
  }

  const categorisedGroups = new Map<string, ITransactionTypeGroup>();
  const uncategorisedTypes: ITransactionTypes[] = [];

  transactionTypes.forEach((type) => {
    const categoryId = resolveCategoryId(type);
    if (!categoryId) {
      uncategorisedTypes.push(type);
      return;
    }
    const group = categorisedGroups.get(categoryId) ?? {
      id: categoryId,
      name: resolveCategoryName(type.category, appLanguage),
      transactionTypes: [],
    };
    group.transactionTypes.push(type);
    categorisedGroups.set(categoryId, group);
  });

  const sortedGroups = [...categorisedGroups.values()].sort((a, b) =>
    a.name.localeCompare(b.name, appLanguage),
  );
  return uncategorisedTypes.length > 0
    ? [
        ...sortedGroups,
        { id: NO_CATEGORY_GROUP_ID, name: noCategoryLabel, transactionTypes: uncategorisedTypes },
      ]
    : sortedGroups;
};

export const buildCategoryFilterOptions = (
  transactionTypes: ITransactionTypes[],
  appLanguage: string,
  noCategoryLabel: string,
): IFilterOption[] => {
  const groups = groupTransactionTypesByCategory(transactionTypes, appLanguage, noCategoryLabel);
  const hasCategorisedGroup = groups.some(({ id }) => id !== NO_CATEGORY_GROUP_ID);
  return hasCategorisedGroup ? groups.map(({ id, name }) => ({ label: name, value: id })) : [];
};

export const filterTransactionTypesByCategory = (
  transactionTypes: ITransactionTypes[],
  selectedGroupIds: string[],
): ITransactionTypes[] => {
  if (selectedGroupIds.length === 0) {
    return transactionTypes;
  }
  const selected = new Set(selectedGroupIds);
  return transactionTypes.filter((type) =>
    selected.has(resolveCategoryId(type) ?? NO_CATEGORY_GROUP_ID),
  );
};
